import {markMustCreatePasswordStorage} from 'app/modules/auth/selective/mustCreatePasswordHelpers'
import {
  getActiveSession,
  getTenantDomain,
  hasPermission,
  isAdmin,
  isSelectiveAiDomains,
} from 'common/HelperFunctions'
import Constants, {SECRET_KEY, USER_TYPES, base_url} from 'common/constants'
import {
  isNetworkError,
  markFirstSuccessfulRequest,
  shouldReportConnectivityError,
} from 'common/helpers/connectivity'
import {isPasswordChangeRequiredError} from 'common/helpers/passwordProvisioning'
import {appendChildIdToUrl, getSelectedChildId} from 'common/helpers/selectedChild'
import {put, select} from 'redux-saga/effects'

import {clearNetworkError, setNetworkError} from '../Actions/Connectivity/connectivity'
import {REDUX_SET_BRANCHES} from '../Actions/General/ActionTypes/ReduxActionTypes'
import {showSampleReadOnlyPopup} from '../Actions/SampleReadOnly/sampleReadOnly'
import {LOGOUT_USER} from '../Actions/SignIn/ActionTypes/ApiActionTypes'
import {REDUX_SET_MUST_CREATE_PASSWORD} from '../Actions/SignIn/ActionTypes/ReduxActionTypes'

// the API's `code` when it refuses a change on the da1timetable sample data (node sampleReadOnly)
const SAMPLE_READ_ONLY_CODE = 'SAMPLE_READ_ONLY'

/** Debug Flags */
const DEBUG = {
  API: false,
  REQUEST: false,
  RAW_RESPONSE: false,
  JSON_RESPONSE: false,
}

const debugLog = (flag, label, value) => {
  if (flag) {
    console.log(`******** ${label} **************`)
    console.log(value)
  }
}

/** Set once we start bouncing the tab to /logout, so parallel 401s don't stack navigations. */
let redirectingToLogout = false

// Clear the latch when the page returns from the back/forward cache (routine on iOS
// Safari), so a 401 can still re-issue the redirect instead of leaving the tab blank.
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) redirectingToLogout = false
  })
}

/** The `logout` guard belongs to the /logout page; anywhere else it is leftover state. */
export const isStaleLogoutGuard = (pathname) =>
  sessionStorage.getItem('logout') === 'true' && !pathname.endsWith('/logout')

/**
 * A dead/expired token makes every authenticated call 401. Send the tab to
 * /logout once, and raise the `logout` guard *before* navigating: that reload
 * re-runs module-level code (the socket connect logger dispatches an API call
 * as soon as the socket is up) which would otherwise fire another authenticated
 * request with the same dead token, 401, and bounce back here before <Logout/>
 * has even mounted to raise the guard itself — a reload loop.
 */
export const redirectToLogout = () => {
  if (redirectingToLogout) return
  redirectingToLogout = true
  sessionStorage.setItem('logout', 'true')
  window.location.href = '/logout'
}

/**
 * Appends user-related IDs into the request body depending on user type and API type
 */
const appendIds = (userInfo, body) => {
  let parentId
  let childId

  if (userInfo?.userType === USER_TYPES.PARENT) {
    parentId = userInfo.user?.id
    childId = getSelectedChildId(userInfo.user?.children)
    if (childId) childId = Number(childId)
  } else if (userInfo?.userType === USER_TYPES.STUDENT) {
    parentId = userInfo.user?.parentOrGuardian?.[0]?.id
    childId = userInfo.user?.id
  }

  if (body instanceof FormData) {
    if (childId) body.append('childId', childId)
    if (parentId) body.append('parentId', parentId)
  } else {
    body ||= {}
    if (childId) body.childId = childId
    if (parentId) body.parentId = parentId
  }

  return body
}

/**
 * Extracts filename from "content-disposition" header
 */
/**
 * `fetch` with the request body's upload progress: `fetch` reports none, so a request that asks
 * for it (`payload.onUploadProgress`, e.g. a data-upload piece) goes through XMLHttpRequest with
 * the very same URL, method, headers and body, and resolves with a `Response` like `fetch` does.
 * A network failure rejects with a TypeError, as `fetch` would.
 *
 * @param {string} url
 * @param {{method: string, headers: Record<string, string>, body?: BodyInit}} request
 * @param {(loaded: number, total: number) => void} onUploadProgress
 * @returns {Promise<Response>}
 */
const fetchWithUploadProgress = (url, request, onUploadProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(request.method, url)
    Object.entries(request.headers || {}).forEach(([name, value]) => {
      if (value !== undefined && value !== null) xhr.setRequestHeader(name, String(value))
    })
    xhr.responseType = 'blob'
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onUploadProgress(event.loaded, event.total)
    }
    xhr.onload = () => {
      const headers = new Headers()
      xhr
        .getAllResponseHeaders()
        .trim()
        .split(/[\r\n]+/)
        .forEach((line) => {
          const at = line.indexOf(':')
          if (at > 0) headers.append(line.slice(0, at).trim(), line.slice(at + 1).trim())
        })
      // A Response cannot carry status 0; a status outside 200-599 is a failed request.
      if (xhr.status < 200 || xhr.status > 599) {
        reject(new TypeError('Network request failed'))
        return
      }
      resolve(new Response(xhr.response, {status: xhr.status, statusText: xhr.statusText, headers}))
    }
    xhr.onerror = () => reject(new TypeError('Network request failed'))
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'))
    xhr.send(request.body ?? null)
  })

const extractFilename = (header) => {
  if (!header) return null
  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(header)
  return match?.[1]?.replace(/['"]/g, '')
}

/**
 * Handles non-JSON (blob) API responses by triggering a file download.
 * `fallbackFilename` (the action's `payload.fileName`) is used when Content-Disposition
 * is missing or not exposed to the browser by CORS.
 */
const handleBlobResponse = function* (data, fallbackFilename) {
  const blob = yield data.blob()
  const blobUrl = URL.createObjectURL(blob)
  const downloadLink = document.createElement('a')

  downloadLink.href = blobUrl
  const filename = extractFilename(data.headers.get('content-disposition')) || fallbackFilename
  if (filename) downloadLink.download = filename

  document.body.appendChild(downloadLink)
  downloadLink.click()
  document.body.removeChild(downloadLink)
  URL.revokeObjectURL(blobUrl)

  return {success: 'true', status: 'true', result: blob}
}

/**
 * Saga: Generic API fetch handler
 */
function* fetchData(action) {
  const state = yield select()
  const userInfo = state.userReducer
  const {type, payload} = action

  // Deduplicate in-flight timetable generation requests.
  // In dev/StrictMode or double-mount scenarios, UI may dispatch the same POST twice.
  // This ensures only one actual network call happens for the same payload,
  // while subsequent requests reuse the first response and still trigger their callbacks.
  const IN_FLIGHT_KEY = '__ILM_TIMETABLE_IN_FLIGHT__'
  const inFlight = (globalThis && globalThis[IN_FLIGHT_KEY]) || {}
  if (globalThis && !globalThis[IN_FLIGHT_KEY]) {
    globalThis[IN_FLIGHT_KEY] = inFlight
  }

  const isTimetableRequest =
    type === 'GET_TIMETABLE' && payload?.apiUrl === Constants.api_request_timetable

  const timetableDedupKey = (() => {
    if (!isTimetableRequest) return null
    const body = payload?.body || {}
    const levelId = body.level_id ?? body.levelId ?? null
    const durationId = body.durationId ?? body.duration_id ?? null
    const model = body.model ?? null
    if (levelId == null || durationId == null) return null
    return `${levelId}:${durationId}:${model}`
  })()

  if (timetableDedupKey && inFlight[timetableDedupKey]?.promise) {
    const result = yield inFlight[timetableDedupKey].promise
    if (result?.ok) {
      payload.onSuccess?.(result.response)
    } else {
      payload.onFailure?.(result.response)
    }
    return
  }

  let resolveDedupPromise = null
  if (timetableDedupKey) {
    const promise = new Promise((resolve) => {
      resolveDedupPromise = resolve
    })
    inFlight[timetableDedupKey] = {promise}
  }

  if (sessionStorage.getItem('logout') === 'true' && type !== LOGOUT_USER) {
    // Leftover flag from a logout that never finished — restart it, or the tab
    // renders an empty app forever instead of reaching /auth/login.
    if (isStaleLogoutGuard(window.location.pathname)) redirectToLogout()
    return
  }

  let completeUrl = ''

  try {
    // --- Build API URL ---
    completeUrl = payload.apiUrl + payload.getString
    if (payload.useBaseURL) {
      completeUrl = base_url + completeUrl
    } else if (payload.useLgsURL) {
      completeUrl = `https://api-lgs.ilmversity.net/api/v1/${payload.apiUrl}${payload.getString}`
    } else if (payload.useLegacyURL) {
      completeUrl = import.meta.env.VITE_APP_LEGACY_API_URL + completeUrl
    }

    // GETs carry no body, so a parent's selected child rides on the query
    // string instead (the API verifies it belongs to the parent).
    if (payload.useBaseURL && payload.requestType === 'GET') {
      completeUrl = appendChildIdToUrl(userInfo, completeUrl)
    }

    debugLog(DEBUG.API, `${payload.requestType} API URL`, completeUrl)

    // --- Build Request ---
    let {body} = payload
    const header2 = payload.header2 || {}
    const request = {
      method: payload.requestType,
      headers: {
        Accept: 'application/json',
        platform: 'web',
        platformKey: SECRET_KEY.web,
        version: '1.0.0',
        ...header2,
      },
    }

    if (!payload.omitDomainHeader && !request.headers.domain) {
      request.headers.domain = getTenantDomain()
    }

    const activeSession = getActiveSession()
    if (activeSession?.db_postfix) {
      request.headers['session-postfix'] = activeSession.db_postfix
    }

    if (!(body instanceof FormData)) {
      request.headers['Content-Type'] = payload.header
    }

    const branchId = localStorage.getItem('branch')
    if (branchId) request.headers['branch-id'] = branchId

    if (userInfo?.accessToken && !payload.noAccessToken) {
      request.headers.Authorization = `Bearer ${userInfo.accessToken}`
    }

    if (payload.requestType !== 'GET') {
      body = appendIds(userInfo, body)
      request.body = ['application/x-www-form-urlencoded', 'multipart/form-data'].includes(
        payload.header
      )
        ? body
        : JSON.stringify(body)

      // The bulk-import payload can get large (classes/students/subjects); on the AI
      // domains that API accepts a gzip-compressed body (Content-Encoding header tells
      // the server to inflate). Only this endpoint — every other API sends plain JSON.
      // Falls back to the plain body on browsers without CompressionStream.
      if (
        payload.apiUrl === Constants.api_csv_import_bulk &&
        isSelectiveAiDomains() &&
        typeof request.body === 'string' &&
        typeof CompressionStream !== 'undefined'
      ) {
        const gzipStream = new Blob([request.body])
          .stream()
          .pipeThrough(new CompressionStream('gzip'))
        request.body = yield new Response(gzipStream).blob()
        request.headers['Content-Encoding'] = 'gzip'
      }
    }

    debugLog(DEBUG.REQUEST, 'API REQUEST', request)

    if (!base_url) return

    // --- Perform API Call ---
    const data = yield typeof payload.onUploadProgress === 'function'
      ? fetchWithUploadProgress(completeUrl, request, payload.onUploadProgress)
      : fetch(completeUrl, request)
    debugLog(DEBUG.RAW_RESPONSE, 'API RAW RESPONSE', data)

    // --- Parse Response ---
    let response
    const contentType = data.headers.get('content-type')
    if (!data.ok && !payload.htmlResponse && (!contentType || contentType.includes('text/html'))) {
      // A proxy's error page (nginx 413 / 502 / 504), not an API answer: there is no JSON to
      // parse, so hand the failure its HTTP status instead of a SyntaxError.
      response = {status: false, statusCode: data.status, message: data.statusText || ''}
    } else if (
      contentType &&
      !contentType.includes('application/json') &&
      !contentType.includes('text/html')
    ) {
      response = yield* handleBlobResponse(data, payload.fileName)
    } else if (payload.htmlResponse) {
      response = yield data.text()
    } else {
      response = yield data.json()
    }

    debugLog(DEBUG.JSON_RESPONSE, 'API JSON RESPONSE', response)

    // --- Handle Response ---
    if (payload.specialResponseFormat) {
      if (response.statusCode === 200 && !response.error) {
        markFirstSuccessfulRequest()
        yield put(clearNetworkError())
        if (payload.reduxActionType) {
          yield put({type: payload.reduxActionType, payload: response, requestParams: body})
        }
        payload.onSuccess?.(response)
        if (timetableDedupKey && resolveDedupPromise) {
          resolveDedupPromise({ok: true, response})
          delete inFlight[timetableDedupKey]
        }
      } else {
        if (response?.code === SAMPLE_READ_ONLY_CODE) yield put(showSampleReadOnlyPopup())
        payload.onFailure?.(response)
        if (timetableDedupKey && resolveDedupPromise) {
          resolveDedupPromise({ok: false, response})
          delete inFlight[timetableDedupKey]
        }
      }
      return
    }

    const isSuccess =
      response.status === 'true' ||
      response.status === true ||
      response.success === 'true' ||
      response.success === true

    if (isSuccess) {
      markFirstSuccessfulRequest()
      yield put(clearNetworkError())
      // If payload has custom response, use that, otherwise use server response
      if (payload.reduxActionType) {
        const finalPayload = payload.response || response

        if (payload.reduxActionType === REDUX_SET_BRANCHES) {
          finalPayload.userBranches = response.result.filter(
            (branch) => isAdmin() || hasPermission(`branch_permission_${branch.id}`)
          )
        }

        yield put({type: payload.reduxActionType, payload: finalPayload, requestParams: body})
      }
      payload.onSuccess?.(response)
      if (timetableDedupKey && resolveDedupPromise) {
        resolveDedupPromise({ok: true, response})
        delete inFlight[timetableDedupKey]
      }
    } else {
      // System-generated password not yet replaced: the API refuses everything
      // except the password routes. Raising the flag makes the route guard
      // (RequirePasswordCreation) send the user to /create-password.
      if (userInfo?.accessToken && isPasswordChangeRequiredError(response)) {
        markMustCreatePasswordStorage()
        yield put({type: REDUX_SET_MUST_CREATE_PASSWORD, payload: true})
      }
      if (
        userInfo?.accessToken &&
        response.statusCode === 401 &&
        type !== LOGOUT_USER &&
        // Admission applicants hold tokens that only the admission APIs accept;
        // 401s from regular endpoints (socket logs etc.) must not log them out.
        !userInfo.isAdmissionUser
      ) {
        redirectToLogout()
      }
      // A change the page did not guard, refused on the da1timetable sample data: same popup.
      if (response?.code === SAMPLE_READ_ONLY_CODE) yield put(showSampleReadOnlyPopup())
      payload.onFailure?.(
        // commented as per discussion with Bilal and Saqib bhai 30/12/2025
        // completeUrl.includes('-dev') ||
        //   completeUrl.includes('localhost') ||
        //   completeUrl.includes('login')
        //   ? response
        //   : {...response, message: window.intl.$t({id: 'GENERAL.SOMETHING_WENT_WRONG'})}
        response
      )
      if (timetableDedupKey && resolveDedupPromise) {
        resolveDedupPromise({ok: false, response})
        delete inFlight[timetableDedupKey]
      }
    }
  } catch (e) {
    if (timetableDedupKey && resolveDedupPromise) {
      resolveDedupPromise({ok: false, response: e})
      delete inFlight[timetableDedupKey]
    }
    if (isNetworkError(e) && shouldReportConnectivityError()) {
      const message =
        typeof window !== 'undefined' && window.intl?.$t
          ? window.intl.$t({id: 'CONNECTIVITY.API_UNAVAILABLE'})
          : 'Unable to connect. Please check your internet connection.'
      yield put(setNetworkError(message))
    }
    payload.onFailure?.(
      // commented as per discussion with Bilal and Saqib bhai 30/12/2025
      // completeUrl.includes('-dev') ||
      //   completeUrl.includes('localhost') ||
      //   completeUrl.includes('login')
      //   ? e
      //   : {...e, message: window.intl.$t({id: 'GENERAL.SOMETHING_WENT_WRONG'})}
      e
    )
  }
}

export default fetchData
